import { Text } from '@chakra-ui/layout';
import {
  Box,
  Button,
  Card,
  CardProps,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { FC, ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import DownloadIcon from '@/assets/icons/ic-download.svg';

import { ErrorMessage } from '@/components/core/inputs/ErrorMessage';
import { IToastType } from '@/enums/ToastType';
import { useStores } from '@/hooks/useStores';
import { MarketingDocument } from '@/resources/MarketingDocument';
import { EMAIL_REGEX } from '@/utils/constants';

interface IRedCardProps extends CardProps {
  smallHeading: string;
  heading: string;
  slug: string;
}

interface IFormInterface {
  firstName: string;
  lastName: string;
  email: string;
}

const NextChakraLink: FC<{ children?: ReactNode; href: string }> = ({ children, href }) => {
  return (
    <NextLink href={href}>
      <Button fontWeight="normal" fontSize="sm" variant="link">
        {children}
      </Button>
    </NextLink>
  );
};

export const RedCardWithDownloadButton: FC<IRedCardProps> = ({ smallHeading, heading, slug, ...rest }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();
  const { store } = useStores();
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<IFormInterface>();
  const toast = useToast();

  async function onSubmit(props: IFormInterface) {
    try {
      await store.request('marketing_document_emails', 'POST', {
        data: {
          type: MarketingDocument.type,
          id: '',
          attributes: {
            first_name: props.firstName,
            last_name: props.lastName,
            recipient_email: props.email,
            document_slug: slug,
          },
        },
      });

      toast({ status: IToastType.SUCCESS, title: t('newEventPage.downloadPopup.resourceSentToast') });
      onClose();
    } catch (err) {
      toast({ status: IToastType.ERROR, title: t('newEventPage.downloadPopup.resourceSomethingWrong') });
    }
  }

  return (
    <Card mt={12} borderRadius="24px" p={8} w="full" backgroundColor="almostRed.500" {...rest}>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay zIndex="overlay" />

        <ModalContent px={{ base: 8, md: 10, lg: 18 }} maxW={{ base: '626px' }} py={{ base: 5, lg: 12 }}>
          <ModalCloseButton />

          <ModalHeader p={0} mb={8} fontWeight="bold" fontSize="3xl">
            {t('newEventPage.downloadPopup.heading')}
          </ModalHeader>
          <ModalBody p={0}>
            <Flex as="form" flexDirection="column" onSubmit={handleSubmit(onSubmit)}>
              <Text>{t('newEventPage.downloadPopup.description')}</Text>
              <Flex gap={8} mt={8} flexDirection="column">
                <Box>
                  <Input
                    {...register('firstName', {
                      required: { value: true, message: t('newEventPage.downloadPopup.required') },
                    })}
                    type="text"
                    variant="flushed"
                    inputMode="text"
                    aria-invalid={Boolean(errors.firstName)}
                    placeholder={t('newEventPage.downloadPopup.firstNamePlaceholder')}
                  />
                  <ErrorMessage name="firstName" errors={errors} />
                </Box>

                <Box>
                  <Input
                    {...register('lastName', {
                      required: { value: true, message: t('newEventPage.downloadPopup.required') },
                    })}
                    type="text"
                    variant="flushed"
                    inputMode="text"
                    aria-invalid={Boolean(errors.lastName)}
                    placeholder={t('newEventPage.downloadPopup.lastNamePlaceholder')}
                  />
                  <ErrorMessage name="lastName" errors={errors} />
                </Box>

                <Box>
                  <Input
                    {...register('email', {
                      required: { value: true, message: t('profilePage.email.emailRequiredError') },
                      pattern: { value: EMAIL_REGEX, message: t('profilePage.email.emailPatternError') },
                    })}
                    type="text"
                    variant="flushed"
                    inputMode="text"
                    aria-invalid={Boolean(errors.email)}
                    placeholder={t('newEventPage.downloadPopup.emailPlaceholder')}
                  />
                  <ErrorMessage name="email" errors={errors} />
                </Box>
              </Flex>
              <Box mt={8} fontSize="sm" color="gray.600">
                <Text mb={6}>{t('newEventPage.downloadPopup.tos')}</Text>
                <Trans
                  i18nKey="newEventPage.downloadPopup.refer"
                  components={{
                    privacyPolicy: <NextChakraLink href="/privacy-policy" />,
                    contactUs: <NextChakraLink href="/contact-us/support" />,
                  }}
                />
              </Box>
              <Button type="submit" alignSelf="start" px={14} mt={12}>
                {t('newEventPage.downloadPopup.receiveResourceButtonLabel')}
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Flex flexDirection={{ base: 'column', lg: 'row' }} justifyContent="space-between" alignItems="center" gap={8}>
        <Box color="white" textAlign="left" w="full">
          <Text fontFamily="heading" fontWeight="bold" fontSize="sm">
            {smallHeading}
          </Text>
          <Text mt={1} fontFamily="heading" fontWeight="bold" fontSize="xl" lineHeight="normal">
            {heading}
          </Text>
        </Box>
        <Button
          variant="solid"
          colorScheme="almostRed"
          bgColor="almostRed.400"
          color="white"
          alignSelf="flex-start"
          px={{ base: 5, lg: 10 }}
          mt={{ base: 0, lg: 3 }}
          onClick={onOpen}
          rightIcon={<DownloadIcon width="24px" height="24px" color="white" />}
        >
          {t('newEventPage.downloadNow')}
        </Button>
      </Flex>
    </Card>
  );
};
